import React from 'react';
import './Features.css';

/**
 * Features Component
 * Displays key features of lockitup software
 * Each feature shown as a card with icon
 */
function Features() {
  const features = [
    {
      id: 1,
      title: 'Fullscreen Lock',
      description: 'Complete screen coverage with no way to minimize or escape',
      icon: (
        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="8" width="40" height="30" rx="2" stroke="#F1C40F" strokeWidth="2.5" fill="rgba(241, 196, 15, 0.1)" />
          <path d="M5 15H45" stroke="#F1C40F" strokeWidth="2.5" />
          <circle cx="10" cy="11.5" r="1.5" fill="#27AE60" />
          <circle cx="15" cy="11.5" r="1.5" fill="#27AE60" />
          <circle cx="20" cy="11.5" r="1.5" fill="#27AE60" />
          <path d="M12 42L18 38L32 38L38 42" stroke="#F1C40F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8 42H42" stroke="#F1C40F" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      )
    },
    {
      id: 2,
      title: 'Password-Protected',
      description: 'Only unlock with your secure password – no bypass methods',
      icon: (
        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="12" y="22" width="26" height="20" rx="3" stroke="#F1C40F" strokeWidth="2.5" fill="rgba(241, 196, 15, 0.1)" />
          <path d="M17 22V16C17 11.5817 20.5817 8 25 8C29.4183 8 33 11.5817 33 16V22" stroke="#F1C40F" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="25" cy="32" r="3" fill="#27AE60" />
          <path d="M25 35V38" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      )
    },
    {
      id: 3,
      title: 'Blocks Apps & Taskbar',
      description: 'Prevents access to Task Manager, Alt+Tab, and all system shortcuts',
      icon: (
        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="25" cy="25" r="18" stroke="#F1C40F" strokeWidth="2.5" fill="rgba(241, 196, 15, 0.1)" />
          <path d="M16 16L34 34M34 16L16 34" stroke="#27AE60" strokeWidth="3" strokeLinecap="round" />
        </svg>
      )
    },
    {
      id: 4,
      title: 'Auto-Start',
      description: 'Option to run automatically when Windows starts for continuous protection',
      icon: (
        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="25" cy="25" r="18" stroke="#F1C40F" strokeWidth="2.5" fill="rgba(241, 196, 15, 0.1)" />
          <path d="M20 18L32 25L20 32V18Z" fill="#27AE60" stroke="#27AE60" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M25 7V12M25 38V43M7 25H12M38 25H43" stroke="#F1C40F" strokeWidth="2" strokeLinecap="round" />
        </svg>
      )
    },
    {
      id: 5,
      title: 'No Installation',
      description: 'Portable executable – run directly without setup or admin rights',
      icon: (
        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 8H30L40 18V38C40 39.1046 39.1046 40 38 40H15C13.8954 40 13 39.1046 13 38V10C13 8.89543 13.8954 8 15 8Z" stroke="#F1C40F" strokeWidth="2.5" fill="rgba(241, 196, 15, 0.1)" />
          <path d="M30 8V18H40" stroke="#F1C40F" strokeWidth="2.5" strokeLinejoin="round" />
          <path d="M20 26L26 20M26 20L32 26M26 20V34" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    },
    {
      id: 6,
      title: 'Lightweight',
      description: 'Minimal resource usage – won\'t slow down your system',
      icon: (
        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M25 8C25 8 18 15 18 22C18 25.866 21.134 29 25 29C28.866 29 32 25.866 32 22C32 15 25 8 25 8Z" stroke="#F1C40F" strokeWidth="2.5" fill="rgba(241, 196, 15, 0.1)" strokeLinejoin="round" />
          <circle cx="25" cy="38" r="3" fill="#27AE60" />
          <path d="M15 35H35" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      )
    }
  ];

  return (
    <section className="features">
      <div className="container">
        {/* Section Header */}
        <div className="section-header">
          <h2 className="section-title">Powerful Features</h2>
          <p className="section-subtitle">Everything you need to secure your laptop before repair</p>
        </div>

        {/* Features Grid */}
        <div className="features-grid">
          {features.map((feature) => (
            <div key={feature.id} className="feature-card">
              {/* Feature Icon */}
              <div className="feature-icon">
                {feature.icon}
              </div>

              {/* Feature Content */}
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Additional Info Box */}
        <div className="info-box">
          <div className="info-icon">
            <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="15" cy="15" r="13" stroke="#27AE60" strokeWidth="2" />
              <path d="M15 10V16M15 20H15.01" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>
          <div className="info-content">
            <h4>100% Free & Open Source</h4>
            <p>lockitup is completely free to use with no hidden costs, ads, or data collection. Your privacy is our priority.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Features;
